import { useParams, Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import api from '../services/api';
import socket, { connectSocket, joinOrderRoom } from '../services/socket';
import { useCart, clearCartCompletely } from '../context/CartContext';
import { Button, Card } from '../components/ui';
import { trackOrderCompleted } from '../services/analytics';

const Confirmation = () => {
  const { orderId } = useParams();
  const location = useLocation();
  const { dispatch } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(0);

  const paymentMethod = location.state?.paymentMethod;
  const proofUploaded = location.state?.proofUploaded;
  const orderToken = location.state?.orderToken || localStorage.getItem(`orderToken:${orderId}`);

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(vh);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        if (!orderToken) {
          setError('Missing order token. Please keep your order token to track your order.');
          return;
        }

        localStorage.setItem(`orderToken:${orderId}`, orderToken);

        const response = await api.get(`/orders/${orderId}/track`, {
          headers: { 'x-order-token': orderToken },
        });
        setOrder(response.data);

        // Track order completion in PostHog
        trackOrderCompleted({
          id: response.data._id || response.data.id,
          total: response.data.totalAmount,
          itemCount: response.data.items?.length || 0,
          paymentMethod: response.data.paymentMethod,
        });

        // Safety-net: ensure cart is completely cleared once we confirm order exists
        clearCartCompletely();
        dispatch({ type: 'CLEAR' });
      } catch (err) {
        console.error('Failed to fetch order:', err);
        setError('Failed to fetch order details.');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    }
  }, [orderId]);

  useEffect(() => {
    if (!orderId || !orderToken) return;

    connectSocket();

    joinOrderRoom(orderId, orderToken, (resp) => {
      if (!resp?.ok) {
        console.error('Failed to join order room:', resp?.error);
      }
    });

    const refresh = async () => {
      try {
        const response = await api.get(`/orders/${orderId}/track`, {
          headers: { 'x-order-token': orderToken },
        });
        setOrder(response.data);
      } catch (err) {
        console.error('Failed to refresh order:', err);
      }
    };

    socket.on('order-status-updated', (updated) => {
      if (updated?.id === orderId || updated?._id === orderId) {
        refresh();
      }
    });

    socket.on('new-payment-proof', (proof) => {
      if (proof?.orderId === orderId) {
        refresh();
      }
    });

    return () => {
      socket.off('order-status-updated');
      socket.off('new-payment-proof');
      socket.disconnect();
    };
  }, [orderId, orderToken]);

  // Calculate 75% of viewport height minus header
  const headerHeight = 80;
  const contentHeight = viewportHeight > 0 ? Math.floor(viewportHeight * 0.75) : 600;
  const availableHeight = Math.max(contentHeight - headerHeight, 400);

  if (loading) {
    return (
      <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
        <div className="max-w-[700px] mx-auto px-[24px] flex items-center justify-center" style={{ minHeight: `${availableHeight}px` }}>
          <div className="animate-pulse text-light-text-secondary dark:text-brand-text-secondary">Loading order details...</div>
        </div>
      </main>
    );
  }

  if (error || !order) {
    return (
      <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
        <div className="max-w-[700px] mx-auto px-[24px] flex items-center justify-center" style={{ minHeight: `${availableHeight}px` }}>
          <div className="w-full text-center">
            <div className="bg-[#FEE2E2] dark:bg-brand-error/20 border border-brand-error text-brand-error px-[16px] py-[12px] rounded-[12px] mb-[24px]">
              {error || 'Order not found'}
            </div>
            <Link to="/">
              <Button className="bg-black text-white hover:bg-black/90">Return to Home</Button>
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200 pb-[40px]">
      {/* Centered Container with max-width: 1200px */}
      <div className="max-w-[900px] mx-auto px-[16px] md:px-[24px] py-[16px] md:py-[32px]">
        {/* Success Header */}
        <div className="text-center mb-[20px] md:mb-[32px]">
          <div className="w-[72px] h-[72px] mx-auto mb-[20px] bg-[#10B981] rounded-full flex items-center justify-center shadow-[0px_4px_16px_rgba(16,185,129,0.3)]">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          </div>
          <h1 className="text-[28px] md:text-[32px] font-bold text-light-text-primary dark:text-brand-text-primary mb-[8px]">
            Order Confirmed!
          </h1>
          <p className="text-[15px] text-light-text-secondary dark:text-brand-text-secondary">
            Thank you for your order. We'll send you updates on your delivery.
          </p>
        </div>

        {/* Order Details Cards */}
        <div className="space-y-[20px]">
          {/* Order Summary Card */}
          <Card 
            variant="elevated" 
            className="p-[24px] rounded-[18px] bg-white dark:bg-brand-surface border border-[#E5E5E5] dark:border-brand-border-default shadow-[0px_2px_8px_rgba(0,0,0,0.04)]"
          >
            <h2 className="text-[20px] font-bold text-light-text-primary dark:text-brand-text-primary mb-[16px]">
              Order Summary
            </h2>
            <p className="text-[13px] text-light-text-secondary dark:text-brand-text-secondary mb-[20px]">
              Order Reference: <span className="font-mono font-medium text-light-text-primary dark:text-brand-text-primary">{order._id}</span>
            </p>

            {/* Items List */}
            <div className="mb-[20px]">
              <h3 className="text-[16px] font-semibold text-light-text-primary dark:text-brand-text-primary mb-[12px]">Items</h3>
              <div className="space-y-[8px]">
                {order.items.map((i, index) => (
                  <div key={i._id || i.id || `item-${index}`} className="flex justify-between items-center text-[14px] py-[8px] border-b border-[#F0F0F0] dark:border-brand-border-default last:border-0">
                    <span className="text-light-text-secondary dark:text-brand-text-secondary">
                      {i.product?.name || 'Product'} <span className="text-light-text-muted">×{i.quantity}</span>
                    </span>
                    <span className="font-medium text-light-text-primary dark:text-brand-text-primary">
                      UGX {(i.price * i.quantity).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Total Section */}
            <div className="border-t-2 border-[#E5E5E5] dark:border-brand-border-default pt-[16px]">
              <div className="flex justify-between items-baseline">
                <span className="text-[18px] font-bold text-light-text-primary dark:text-brand-text-primary">
                  Total
                </span>
                <span className="text-[24px] font-bold text-brand-accent-red">
                  UGX {order.totalAmount.toLocaleString()}
                </span>
              </div>
            </div>

            {/* Payment & Status Info */}
            <div className="mt-[20px] pt-[20px] border-t border-[#E5E5E5] dark:border-brand-border-default space-y-[12px]">
              <div className="flex justify-between items-center">
                <span className="text-[14px] text-light-text-secondary dark:text-brand-text-secondary">
                  Payment Method
                </span>
                <span className="text-[14px] font-semibold capitalize text-light-text-primary dark:text-brand-text-primary">
                  {order.paymentMethod?.replace('_', ' ')}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[14px] text-light-text-secondary dark:text-brand-text-secondary">
                  Order Status
                </span>
                <span className="text-[14px] font-semibold capitalize text-light-text-primary dark:text-brand-text-primary">
                  {order.status}
                </span>
              </div>
            </div>
          </Card>

          {/* Payment Verification Notice (if applicable) */}
          {proofUploaded && (
            <Card className="p-[20px] rounded-[18px] bg-[#EFF6FF] dark:bg-brand-info/10 border border-[#3B82F6] dark:border-brand-info">
              <div className="flex gap-[12px]">
                <div className="flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                </div>
                <div>
                  <h3 className="text-[16px] font-semibold text-[#1E40AF] dark:text-brand-text-primary mb-[4px]">
                    Payment Verification
                  </h3>
                  <p className="text-[14px] text-[#1E40AF]/80 dark:text-brand-text-secondary leading-[1.5]">
                    Your payment proof has been uploaded successfully. Our team will verify your payment
                    and update your order status. You will receive a notification once confirmed.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Cash on Delivery Notice */}
          {order.paymentMethod === 'cash_on_delivery' && (
            <Card className="p-[20px] rounded-[18px] bg-[#FEF3C7] dark:bg-brand-warning/10 border border-[#F59E0B] dark:border-brand-warning">
              <div className="flex gap-[12px]">
                <div className="flex-shrink-0">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div>
                  <h3 className="text-[16px] font-semibold text-[#92400E] dark:text-brand-text-primary mb-[4px]">
                    Cash on Delivery
                  </h3>
                  <p className="text-[14px] text-[#92400E]/80 dark:text-brand-text-secondary leading-[1.5]">
                    Please have the exact amount ready when your order arrives. Our delivery personnel
                    will collect the payment upon delivery.
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Delivery Information Card */}
          {order.deliveryInfo && (
            <Card className="p-[24px] rounded-[18px] bg-white dark:bg-brand-surface border border-[#E5E5E5] dark:border-brand-border-default shadow-[0px_2px_8px_rgba(0,0,0,0.04)]">
              <h2 className="text-[18px] font-bold text-light-text-primary dark:text-brand-text-primary mb-[16px]">
                Delivery Information
              </h2>
              <div className="space-y-[10px]">
                <div className="flex">
                  <span className="text-[14px] text-light-text-muted dark:text-brand-text-muted w-[100px] flex-shrink-0">
                    Recipient:
                  </span>
                  <span className="text-[14px] text-light-text-primary dark:text-brand-text-primary font-medium">
                    {order.deliveryInfo.recipientName}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-[14px] text-light-text-muted dark:text-brand-text-muted w-[100px] flex-shrink-0">
                    Phone:
                  </span>
                  <span className="text-[14px] text-light-text-primary dark:text-brand-text-primary font-medium">
                    {order.deliveryInfo.phone}
                  </span>
                </div>
                <div className="flex">
                  <span className="text-[14px] text-light-text-muted dark:text-brand-text-muted w-[100px] flex-shrink-0">
                    Address:
                  </span>
                  <span className="text-[14px] text-light-text-primary dark:text-brand-text-primary font-medium">
                    {order.deliveryInfo.address}
                  </span>
                </div>
                {order.deliveryInfo.instructions && (
                  <div className="flex">
                    <span className="text-[14px] text-light-text-muted dark:text-brand-text-muted w-[100px] flex-shrink-0">
                      Instructions:
                    </span>
                    <span className="text-[14px] text-light-text-primary dark:text-brand-text-primary font-medium">
                      {order.deliveryInfo.instructions}
                    </span>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>

        {/* Action Button */}
        <div className="flex justify-center mt-[32px]">
          <Link to="/catalog">
            <Button className="h-[56px] px-[40px] text-[16px] rounded-[12px] bg-black text-white font-semibold hover:bg-black/90 active:scale-[0.98] transition-all">
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </main>
  );
};

export default Confirmation;
